<div class="row mb-3 mb-md-4 g-3 g-md-4">
  @if (providerList$ | async; as providerList) {
    @for (provider of providerList; track provider) {
      <ng-container
        [ngTemplateOutlet]="providerTemplate"
        [ngTemplateOutletContext]="{ $implicit: provider }"></ng-container>
    }
  } @else {
    <div class="row mb-3 mb-md-4 g-3 g-md-4">
      <div class="col-12 col-sm-auto">
        <div class="card accounting-card text-center">
          <div class="card-body">
            <app-loader></app-loader>
          </div>
        </div>
      </div>
    </div>
  }
</div>

<ng-template
  #providerTemplate
  let-provider>
  @if (activeProvider() === provider.name && !connectionPending()) {
    <!-- Large Card -->
    <div class="col-12 order-0'">
      <div class="card accounting-card accounting-card-lg">
        <div class="card-body">
          <div class="row">
            <div class="col-auto">
              <div class="accounting-logo lg-bordered">
                <img
                  [ngSrc]="'./assets/accounting-logos/' + provider.logo.img"
                  [width]="provider.logo.width"
                  [height]="provider.logo.height"
                  alt="{{ provider.name }}" />
              </div>
            </div>
            <div class="col-auto">
              <div
                class="card-title"
                id="title{{ provider.name }}">
                {{ provider.name }}
              </div>
              @if (!sourceAccount() && connectionComplete()) {
                <app-status-badge
                  value="noSourceAccount"
                  [department]="statusBadgeDepartments.INTEGRATIONS"></app-status-badge>
              } @else {
                <app-status-badge
                  [value]="connectionStatus()"
                  [department]="statusBadgeDepartments.INTEGRATIONS"></app-status-badge>
              }
            </div>
            <div class="col-auto ms-auto">
              @if (accountingDisconnected()) {
                <button
                  class="btn btn-primary"
                  (click)="onConnect(provider.name)">
                  Connect
                  @if (
                    (xeroBusy() && provider.name === AccountingProviderCompany.XERO) ||
                    (qbBusy() && provider.name === AccountingProviderCompany.QUICKBOOKS)
                  ) {
                    <span class="spinner-border spinner-border-sm ms-2"></span>
                  }
                </button>
              } @else {
                @if (connectionError() || connectionStatus() === AccountingConnectionStatus.Pending) {
                  <button
                    class="btn btn-outline-dark btn-sm"
                    (click)="reconnectConnection(provider.name)">
                    <i class="text-warning iconoir-warning-circle me-1"></i> Reconnect
                  </button>
                } @else {
                  <button
                    class="btn btn-outline-dark btn-sm"
                    (click)="deleteConnection()">
                    Disconnect
                  </button>
                }
              }
            </div>
          </div>
          <hr class="mt-0 mb-3" />

          <!-- Provider account details -->
          <div class="row row-cols-1 row-cols-md-2 g-3 mb-3">
            <div class="col d-flex flex-column">
              <p class="form-label">Linked organisation</p>
              <div
                class="card card-sm integration-card mt-auto"
                [ngClass]="organisationNotSelected() ? 'border-warning' : 'border-secondary'">
                <div class="card-body p-2">
                  <div class="row align-items-center flex-nowrap">
                    <div class="col">
                      <div class="row align-items-center">
                        <div class="col-auto pe-0">
                          <i
                            class="iconoir-city fs-3 me-2"
                            [ngClass]="organisationNotSelected() ? 'text-warning' : 'text-secondary'"></i>
                        </div>
                        <div class="col">
                          {{ organisationNotSelected() ? '-' : selectedTenant()?.name }}
                        </div>
                      </div>
                    </div>
                    @if (organisationNotSelected()) {
                      <div class="col-auto">
                        <a
                          routerLink="../connect/select-organisation"
                          class="btn btn-light-on-white btn-sm">
                          Link organisation
                        </a>
                      </div>
                    }
                  </div>
                </div>
              </div>
              @if (organisationNotSelected()) {
                <div class="small text-warning mt-1">Please select an organisation.</div>
              }
            </div>
            <div class="col">
              <p class="form-label">
                Linked bank account
                <i
                  class="iconoir-info-circle text-muted ms-3"
                  ngbTooltip="The bank account used to deposit money into Paystashio."
                  tooltipClass="custom-tooltip"
                  placement="bottom"></i>
              </p>
              <div
                class="card card-sm integration-card"
                [ngClass]="sourceAccount() ? 'border-secondary' : 'border-warning'">
                <div class="card-body p-2">
                  <div class="row align-items-center flex-nowrap">
                    <div class="col">
                      <div class="row align-items-center">
                        <div class="col-auto">
                          <i
                            class="pays-svg-icon ic-bank"
                            [ngClass]="!sourceAccount() ? 'text-warning' : 'text-secondary'"></i>
                        </div>
                        <div class="col">
                          {{ sourceAccount() ? sourceAccount()?.name : '-' }}
                        </div>
                      </div>
                    </div>
                    <div class="col-auto">
                      <a
                        routerLink="../connect/select-source-account"
                        class="btn btn-light-on-white btn-sm">
                        {{ sourceAccount() ? 'Change' : 'Link account' }}
                      </a>
                    </div>
                  </div>
                </div>
              </div>

              @if (organisationNotSelected()) {
                <div class="small text-warning mt-1">Please select a bank account.</div>
              }
            </div>
          </div>
          @if (contraAccounts().length) {
            <p class="form-label">Paystashio account details</p>
            <div class="row row-cols-1 row-cols-md-2 g-3">
              <div class="col">
                <div class="card card-sm border-secondary">
                  <div class="card-body">
                    <span class="text-muted">Name:</span> <br />
                    <span class="fw-semibold">{{ contraAccounts()[0].name }}</span>
                  </div>
                </div>
              </div>
              <div class="col">
                <div class="card card-sm border-secondary">
                  <div class="card-body">
                    <span class="text-muted">Name:</span> <br />
                    <span class="fw-semibold">{{ contraAccounts()[1].name }}</span>
                  </div>
                </div>
              </div>
            </div>
          }
        </div>
      </div>
    </div>
  } @else {
    <!-- Smaller cards -->
    <div class="col-12 col-sm-auto">
      <div
        class="card accounting-card"
        [ngClass]="{ 'accounting-card-sm': activeProvider() && !connectionPending() }">
        <div class="card-body">
          <div class="row">
            <div class="col-auto">
              <div class="accounting-logo lg-bordered">
                <img
                  [ngSrc]="'./assets/accounting-logos/' + provider.logo.img"
                  [width]="provider.logo.width"
                  [height]="provider.logo.height"
                  alt="{{ provider.name }}" />
              </div>
            </div>
          </div>
          <div
            class="card-title"
            id="title_{{ provider.name }}">
            {{ provider.name }}
          </div>
          @if (
            (hasAccounting() && !activeProvider()) ||
            (activeProvider() && connectionPending()) ||
            accountingDisconnected()
          ) {
            <div class="row align-items-center">
              <div class="col-auto">
                <button
                  class="btn btn-primary"
                  (click)="onConnect(provider.name)">
                  Connect
                  @if (
                    (xeroBusy() && provider.name === AccountingProviderCompany.XERO) ||
                    (qbBusy() && provider.name === AccountingProviderCompany.QUICKBOOKS)
                  ) {
                    <span class="spinner-border spinner-border-sm ms-2"> </span>
                  }
                </button>
              </div>
            </div>
          }
        </div>
      </div>
    </div>
  }
</ng-template>

<router-outlet />

@if (!subscriptionLoading() && !hasAccounting()) {
  <app-upgrade-to-premium-overlay type="integration"></app-upgrade-to-premium-overlay>
}
